import jsPDF from "jspdf";
import type { EndorsementInput } from "@/types";
import { PAGE_W, MARGIN_X, CONTENT_W, ensureSpace } from "./pdf-utils";

export function drawProfilePage(doc: jsPDF, input: EndorsementInput): void {
  doc.addPage();
  let y = 18;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  const photoSize = 30;
  const nameMaxW = input.photoDataUrl ? CONTENT_W - photoSize - 6 : CONTENT_W;
  const nameLines = doc.splitTextToSize(input.candidateName, nameMaxW);
  doc.text(nameLines, MARGIN_X, y + 8);

  if (input.photoDataUrl) {
    try {
      doc.addImage(input.photoDataUrl, "JPEG", PAGE_W - MARGIN_X - photoSize, y, photoSize, photoSize);
    } catch { /* ignore */ }
  }

  y += Math.max(photoSize + 4, nameLines.length * 8 + 6);
  doc.setLineWidth(0.6);
  doc.line(MARGIN_X, y, PAGE_W - MARGIN_X, y);
  y += 8;

  if (input.careerObjective?.trim()) {
    y = ensureSpace(doc, y, 20);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text("Career Objective:", MARGIN_X, y);
    y += 5;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    const lines = doc.splitTextToSize(input.careerObjective.trim(), CONTENT_W);
    doc.text(lines, MARGIN_X, y);
    y += lines.length * 4.5 + 5;
  }

  if (input.personal && Object.values(input.personal).some((v) => v)) {
    y = ensureSpace(doc, y, 60);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text("PERSONAL DATA:", MARGIN_X, y);
    y += 6;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    const rows: Array<[string, string | number | undefined]> = [
      ["Birth Date", input.personal.birth_date],
      ["Birth Place", input.personal.birth_place],
      ["Age", input.personal.age],
      ["Height", input.personal.height],
      ["Weight", input.personal.weight],
      ["Nationality", input.personal.nationality],
      ["Civil Status", input.personal.civil_status],
      ["Religion", input.personal.religion],
      ["Mother Name", input.personal.mother_name],
      ["Father Name", input.personal.father_name],
    ];
    for (const [k, v] of rows) {
      if (!v) continue;
      y = ensureSpace(doc, y, 6);
      doc.text(k, MARGIN_X, y);
      doc.text(":", MARGIN_X + 35, y);
      doc.text(String(v), MARGIN_X + 40, y);
      y += 5;
    }
    y += 3;
  }

  if (input.education && input.education.length > 0) {
    y = ensureSpace(doc, y, 14);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text("Educational Background:", MARGIN_X, y);
    y += 6;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    for (const e of input.education) {
      y = ensureSpace(doc, y, 12);
      doc.setFont("helvetica", "bold");
      doc.text(e.school || "—", MARGIN_X, y);
      doc.setFont("helvetica", "normal");
      if (e.year) doc.text(e.year, PAGE_W - MARGIN_X, y, { align: "right" });
      y += 5;
      if (e.degree) {
        const lines = doc.splitTextToSize(e.degree, CONTENT_W);
        doc.text(lines, MARGIN_X, y);
        y += lines.length * 4.5;
      }
      y += 3;
    }
    y += 3;
  }

  if (input.work && input.work.length > 0) {
    y = ensureSpace(doc, y, 14);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text("WORKING EXPERIENCE:", MARGIN_X, y);
    y += 6;
    for (const w of input.work) {
      y = ensureSpace(doc, y, 18);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.text(w.company || "—", MARGIN_X, y);
      const range = [w.from, w.to].filter(Boolean).join(" – ");
      if (range) doc.text(range, PAGE_W - MARGIN_X, y, { align: "right" });
      y += 4.5;
      if (w.title) { doc.setFont("helvetica", "italic"); doc.text(w.title, MARGIN_X, y); y += 4.5; }
      if (w.description) {
        doc.setFont("helvetica", "normal");
        const lines = doc.splitTextToSize(w.description, CONTENT_W);
        for (const line of lines) { y = ensureSpace(doc, y, 5); doc.text(line, MARGIN_X, y); y += 4.5; }
      }
      y += 3;
    }
    y += 3;
  }

  if (input.training && input.training.length > 0) {
    y = ensureSpace(doc, y, 14);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text("SEMINAR / TRAINING ATTENDED:", MARGIN_X, y);
    y += 6;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    for (const t of input.training) {
      y = ensureSpace(doc, y, 6);
      const lines = doc.splitTextToSize(`• ${t}`, CONTENT_W);
      doc.text(lines, MARGIN_X, y);
      y += lines.length * 4.5 + 1;
    }
  }
}
